import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { API } from "../App";
import {
  AdminUsersTab,
  AdminProductsTab,
  AdminOrdersTab,
  AdminAnalyticsTab,
  AdminEmailsTab,
  AdminBlogTab,
  AdminCommentsTab
} from "../components/admin";

export default function Admin() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("users");
  const [data, setData] = useState(null);
  const [productsData, setProductsData] = useState(null);
  const [ordersData, setOrdersData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({
    recipient_group: "all",
    subject: "",
    body: ""
  });
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  // Blog state
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogComments, setBlogComments] = useState([]);
  const [pendingCommentsCount, setPendingCommentsCount] = useState(0);

  const adminKey = searchParams.get("key");

  useEffect(() => {
    if (!adminKey) {
      setError("Admin key required. Add ?key=YOUR_ADMIN_KEY to the URL");
      setLoading(false);
      return;
    }
    fetchUsers();
    fetchProducts();
    fetchOrders();
    fetchAnalytics();
    fetchBlogPosts();
    fetchBlogComments();
  }, [adminKey]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users?admin_key=${adminKey}`);
      if (!response.ok) {
        if (response.status === 403) throw new Error("Invalid admin key");
        throw new Error("Failed to fetch users");
      }
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API}/admin/products?admin_key=${adminKey}`);
      if (response.ok) {
        const result = await response.json();
        setProductsData(result);
      }
    } catch (err) {
      console.error("Failed to fetch products:", err);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API}/admin/prompt-analytics?admin_key=${adminKey}`);
      if (response.ok) {
        const result = await response.json();
        setAnalyticsData(result);
      }
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API}/admin/orders?admin_key=${adminKey}`);
      if (response.ok) {
        const result = await response.json();
        setOrdersData(result);
      }
    } catch (err) {
      console.error("Failed to fetch orders:", err);
    }
  };

  const fetchBlogPosts = async () => {
    try {
      const response = await fetch(`${API}/admin/blog/posts?admin_key=${adminKey}`);
      if (response.ok) {
        const result = await response.json();
        setBlogPosts(result.posts || []);
      }
    } catch (err) {
      console.error("Failed to fetch blog posts:", err);
    }
  };

  const fetchBlogComments = async () => {
    try {
      const response = await fetch(`${API}/admin/blog/comments?admin_key=${adminKey}&status=pending`);
      if (response.ok) {
        const result = await response.json();
        setBlogComments(result.comments || []);
        setPendingCommentsCount(result.pending_count || 0);
      }
    } catch (err) {
      console.error("Failed to fetch blog comments:", err);
    }
  };

  const deleteUser = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}?admin_key=${adminKey}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error("Failed to delete user");
      await fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const updatePlan = async (userId, plan) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/plan?admin_key=${adminKey}&plan=${plan}`, {
        method: 'PUT'
      });
      if (!response.ok) throw new Error("Failed to update plan");
      await fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const exportUsers = () => {
    window.open(`${API}/admin/export?admin_key=${adminKey}`, '_blank');
  };

  const sendBulkEmail = async () => {
    if (!emailForm.subject.trim() || !emailForm.body.trim()) {
      alert("Please fill in subject and message");
      return;
    }
    
    setEmailSending(true);
    setEmailResult(null);
    
    try {
      const response = await fetch(`${API}/admin/email?admin_key=${adminKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailForm)
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || "Failed to send emails");
      }
      
      setEmailResult(result);
    } catch (err) {
      setEmailResult({ error: err.message });
    } finally {
      setEmailSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric"
      });
    } catch { return "—"; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#A87C6D] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#6E6A66]">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-medium text-[#2D2A26] mb-2">Access Denied</h2>
          <p className="text-[#6E6A66]">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-[#2D2A26] mb-4">Send Bulk Email</h3>
            
            {emailResult ? (
              <div className="text-center py-6">
                {emailResult.error ? (
                  <>
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <p className="text-red-600 mb-4">{emailResult.error}</p>
                  </>
                ) : (
                  <>
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-[#2D2A26] font-medium mb-2">Emails Sent!</p>
                    <p className="text-[#6E6A66]">
                      Successfully sent to {emailResult.sent} of {emailResult.total_recipients} recipients
                      {emailResult.failed > 0 && <span className="text-red-500"> ({emailResult.failed} failed)</span>}
                    </p>
                  </>
                )}
                <button
                  onClick={() => {
                    setShowEmailModal(false);
                    setEmailResult(null);
                    setEmailForm({ recipient_group: "all", subject: "", body: "" });
                  }}
                  className="mt-4 px-6 py-2 rounded-lg bg-[#2D2A26] text-white hover:bg-[#3D3A36]"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {/* Recipients */}
                  <div>
                    <label className="block text-sm font-medium text-[#6E6A66] mb-2">Send to</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEmailForm({...emailForm, recipient_group: "all"})}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm ${
                          emailForm.recipient_group === "all" 
                            ? "bg-[#2D2A26] text-white" 
                            : "bg-[#F5F2EE] text-[#6E6A66] hover:bg-[#E8E4DE]"
                        }`}
                      >
                        All ({data?.stats?.total_users || 0})
                      </button>
                      <button
                        onClick={() => setEmailForm({...emailForm, recipient_group: "free"})}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm ${
                          emailForm.recipient_group === "free" 
                            ? "bg-[#6E6A66] text-white" 
                            : "bg-[#F5F2EE] text-[#6E6A66] hover:bg-[#E8E4DE]"
                        }`}
                      >
                        Open ({data?.stats?.free_users || 0})
                      </button>
                      <button
                        onClick={() => setEmailForm({...emailForm, recipient_group: "premium"})}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm ${
                          emailForm.recipient_group === "premium" 
                            ? "bg-[#A87C6D] text-white" 
                            : "bg-[#F5F2EE] text-[#6E6A66] hover:bg-[#E8E4DE]"
                        }`}
                      >
                        Inner Circle ({data?.stats?.premium_users || 0})
                      </button>
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-[#6E6A66] mb-2">Subject</label>
                    <input
                      type="text"
                      value={emailForm.subject}
                      onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                      placeholder="Email subject..."
                      className="w-full px-4 py-2 rounded-lg border border-[#E8E4DE] focus:outline-none focus:ring-2 focus:ring-[#A87C6D]/20 focus:border-[#A87C6D]"
                    />
                  </div>

                  {/* Body */}
                  <div>
                    <label className="block text-sm font-medium text-[#6E6A66] mb-2">
                      Message <span className="font-normal text-[#A8A4A0]">(use {"{{name}}"} for personalization)</span>
                    </label>
                    <textarea
                      value={emailForm.body}
                      onChange={(e) => setEmailForm({...emailForm, body: e.target.value})}
                      placeholder="Hi {{name}},&#10;&#10;Write your message here..."
                      rows={6}
                      className="w-full px-4 py-3 rounded-lg border border-[#E8E4DE] focus:outline-none focus:ring-2 focus:ring-[#A87C6D]/20 focus:border-[#A87C6D] resize-none"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowEmailModal(false);
                      setEmailForm({ recipient_group: "all", subject: "", body: "" });
                    }}
                    className="flex-1 px-4 py-2 rounded-lg border border-[#E8E4DE] text-[#6E6A66] hover:bg-[#F5F2EE]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendBulkEmail}
                    disabled={emailSending || !emailForm.subject.trim() || !emailForm.body.trim()}
                    className="flex-1 px-4 py-2 rounded-lg bg-[#A87C6D] text-white hover:bg-[#96695C] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {emailSending ? "Sending..." : "Send Email"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <header className="border-b border-[#E8E4DE] bg-white/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-light text-[#2D2A26]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Admin Dashboard
            </h1>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowEmailModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#A87C6D] text-white hover:bg-[#96695C] text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send Email
              </button>
              <button
                onClick={exportUsers}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#E8E4DE] text-[#6E6A66] hover:bg-[#F5F2EE] text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export CSV
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#E8E4DE] overflow-x-auto">
          <button
            onClick={() => setActiveTab("users")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
              activeTab === "users" 
                ? "border-[#A87C6D] text-[#A87C6D]" 
                : "border-transparent text-[#6E6A66] hover:text-[#2D2A26]"
            }`}
          >
            Users ({data?.stats?.total_users || 0})
          </button>
          <button
            onClick={() => setActiveTab("products")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
              activeTab === "products" 
                ? "border-[#A87C6D] text-[#A87C6D]" 
                : "border-transparent text-[#6E6A66] hover:text-[#2D2A26]"
            }`}
          >
            Products ({productsData?.total || 0})
            {productsData?.low_stock_count > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">
                {productsData.low_stock_count} low
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("orders")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
              activeTab === "orders" 
                ? "border-[#A87C6D] text-[#A87C6D]" 
                : "border-transparent text-[#6E6A66] hover:text-[#2D2A26]"
            }`}
          >
            Orders ({ordersData?.stats?.total_orders || 0})
            {ordersData?.stats?.pending_shipment > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                {ordersData.stats.pending_shipment} pending
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("analytics")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
              activeTab === "analytics" 
                ? "border-[#A87C6D] text-[#A87C6D]" 
                : "border-transparent text-[#6E6A66] hover:text-[#2D2A26]"
            }`}
          >
            Prompt Analytics
          </button>
          <button
            onClick={() => setActiveTab("emails")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
              activeTab === "emails" 
                ? "border-[#A87C6D] text-[#A87C6D]" 
                : "border-transparent text-[#6E6A66] hover:text-[#2D2A26]"
            }`}
          >
            Automated Emails
          </button>
          <button
            onClick={() => setActiveTab("blog")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
              activeTab === "blog" 
                ? "border-[#A87C6D] text-[#A87C6D]" 
                : "border-transparent text-[#6E6A66] hover:text-[#2D2A26]"
            }`}
          >
            Blog ({blogPosts.length})
          </button>
          <button
            onClick={() => setActiveTab("comments")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
              activeTab === "comments" 
                ? "border-[#A87C6D] text-[#A87C6D]" 
                : "border-transparent text-[#6E6A66] hover:text-[#2D2A26]"
            }`}
          >
            Comments
            {pendingCommentsCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                {pendingCommentsCount} pending
              </span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "users" && (
          <AdminUsersTab 
            data={data} 
            formatDate={formatDate} 
            onDeleteUser={deleteUser}
            onUpdatePlan={updatePlan}
          />
        )}

        {activeTab === "products" && (
          <AdminProductsTab 
            productsData={productsData} 
            adminKey={adminKey} 
            onRefresh={fetchProducts}
          />
        )}

        {activeTab === "orders" && (
          <AdminOrdersTab 
            ordersData={ordersData} 
            adminKey={adminKey} 
            onRefresh={fetchOrders}
          />
        )}

        {activeTab === "analytics" && (
          <AdminAnalyticsTab analyticsData={analyticsData} />
        )}

        {activeTab === "emails" && (
          <AdminEmailsTab adminKey={adminKey} />
        )}

        {activeTab === "blog" && (
          <AdminBlogTab 
            blogPosts={blogPosts} 
            adminKey={adminKey} 
            onRefresh={fetchBlogPosts}
          />
        )}

        {activeTab === "comments" && (
          <AdminCommentsTab 
            blogComments={blogComments} 
            pendingCommentsCount={pendingCommentsCount}
            adminKey={adminKey} 
            onRefresh={fetchBlogComments}
          />
        )}
      </main>
    </div>
  );
}
